/**
 * The durable half of a chat: the session row and its persisted transcript.
 *
 * Everything here is Apollo-cached and survives a reload. The in-flight turn lives
 * in `useAgentTurn` and never touches the cache.
 */
import { useQuery } from "@apollo/client/react";
import type { AgentMessagesQuery } from "@repo/graphql/documents/agent/query.generated";
import type { GuestAgentMessagesQuery } from "@repo/graphql/documents/guest/query.generated";
import { useCallback, useMemo } from "react";
import { reconcileAll } from "../../shared/agent/reconcile";
import { readRow } from "../../shared/agent/readRow";
import type { AgentChatMessage } from "../../shared/agent/types";
import useChatDocuments from "./useChatDocuments";

/**
 * Rows per page. Note these are ROWS, not bubbles: tool calls and tool returns are
 * rows with an empty `text` and are never rendered, so a page of 40 can easily be
 * fewer than 40 bubbles.
 */
export const MESSAGE_PAGE_SIZE = 40;

type MessagesData = AgentMessagesQuery | GuestAgentMessagesQuery;

type MessageNode = MessagesData["agent"]["messages"]["edges"][number]["node"];

const nodesOf = (data: MessagesData | undefined): MessageNode[] =>
  (data?.agent.messages.edges ?? []).map((edge) => edge.node);

const useAgentSession = (sessionId: string | undefined) => {
  const skip = !sessionId;
  const documents = useChatDocuments();

  const sessionQuery = useQuery(documents.agentSession, {
    variables: { id: sessionId ?? "" },
    skip,
    // Never cache-first: a stale `isRunning: true` from ten minutes ago would
    // phantom-lock the composer on a chat that finished long ago.
    fetchPolicy: "cache-and-network",
  });

  const messagesQuery = useQuery(documents.agentMessages, {
    variables: { session_Id: sessionId ?? "", first: MESSAGE_PAGE_SIZE },
    skip,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const session = sessionQuery.data?.agent.get;
  const connection = messagesQuery.data?.agent.messages;
  const nodes = useMemo(
    () => nodesOf(messagesQuery.data),
    [messagesQuery.data],
  );

  /**
   * The transcript, oldest first.
   *
   * The server serves it newest-first so the tail can be fetched without knowing
   * how long the thread is; it is reversed here because that is the order a chat
   * reads in. Rows with no text are tool calls and tool returns: persisted so the
   * model can replay the conversation, but not transcript.
   */
  const messages = useMemo<AgentChatMessage[]>(
    () =>
      nodes
        .map((node) => ({
          id: node.id,
          role:
            node.kind === "REQUEST"
              ? ("user" as const)
              : ("assistant" as const),
          text: node.text,
          createdAt: node.createdAt,
          pending: false,
          ...readRow(node.question, node.answer),
        }))
        // A row with no text and no card is a tool call or a tool return: persisted so
        // the model can replay the conversation, but not transcript. An old question
        // row is kept only when it carries the answer — that row shows the prompt too.
        .filter(
          (message) =>
            message.text !== "" ||
            !!message.legacy?.answer ||
            message.form !== null ||
            message.concept !== null,
        )
        .reverse(),
    [nodes],
  );

  /**
   * Has this run's answer landed in the cache?
   *
   * `useAgentChat` hides the streamed copy in the same frame the persisted one
   * appears, so the answer is never on screen twice. Matching on RESPONSE-with-text is
   * essential rather than incidental: the user's own REQUEST row carries the *same* run
   * id, so a bare run-id check would hide the assistant's bubble the moment the user's
   * row arrived, and the answer would blink out of existence until the next render.
   */
  const hasAnswerFor = useCallback(
    (runId: string | null): boolean =>
      !!runId &&
      nodes.some(
        (node) =>
          node.run?.id === runId &&
          node.kind === "RESPONSE" &&
          node.text !== "",
      ),
    [nodes],
  );

  /** Has the user's own turn been confirmed, so the local echo can retire? */
  const hasEchoFor = useCallback(
    (runId: string | null): boolean =>
      !!runId &&
      nodes.some(
        (node) =>
          node.run?.id === runId && node.kind === "REQUEST" && node.text !== "",
      ),
    [nodes],
  );

  /**
   * Re-read the thread from the server.
   *
   * The one place durable state is refreshed, called from four situations: a run
   * completed, a run failed, the socket reconnected, and the stall watchdog gave up.
   *
   * The reconnect case is the one that justifies it. A resume replays what the stream
   * still holds, but the stream keeps its events for a bounded time and a reload has
   * no resume point at all, so a run that finished while the socket was down can leave
   * the user with a frozen half-sentence and a locked composer while the finished
   * answer sits in Postgres.
   *
   * Never rejects — see `reconcileAll`: the read is a refresh, not a verdict on the
   * action that asked for it.
   *
   * KNOWN LIMITATION: the refetch is cursorless, and Apollo's `relayStylePagination`
   * merge REPLACES the edge list when it sees neither `after` nor `before`. So if the
   * user had paged back through history, reconciling collapses the list to the newest
   * page again. It is a scroll position lost, not data lost — `hasNextPage` goes back
   * to true and `loadOlder` re-fetches — and it only bites someone who is reading
   * scrollback at the exact moment a turn ends. Splicing only the new rows in by hand
   * would mean fabricating relay cursors for them, which corrupts the connection's
   * own pageInfo; the honest fix is a `sequence > n` filter on the backend, which is
   * not worth it until someone actually complains.
   */
  const reconcile = useCallback(async () => {
    if (!sessionId) return;
    await reconcileAll([
      () => sessionQuery.refetch(),
      () =>
        messagesQuery.refetch({
          session_Id: sessionId,
          first: MESSAGE_PAGE_SIZE,
        }),
    ]);
  }, [sessionQuery, messagesQuery, sessionId]);

  const pageInfo = connection?.pageInfo;

  /** Page backwards through history — older rows, never newer ones. */
  const loadOlder = useCallback(async () => {
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;
    await messagesQuery.fetchMore({
      variables: { after: pageInfo.endCursor, first: MESSAGE_PAGE_SIZE },
    });
  }, [messagesQuery, pageInfo]);

  return {
    session,
    messages,
    hasAnswerFor,
    hasEchoFor,
    reconcile,
    loadOlder,
    hasOlder: !!pageInfo?.hasNextPage,
    loading: sessionQuery.loading || messagesQuery.loading,
    error: sessionQuery.error ?? messagesQuery.error,
  };
};

export default useAgentSession;
